import { ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { roleLabel } from '../lib/roles';
import { PhaseChip } from './ui/Pill';
import { canSee, Feature } from '../lib/features';
import logo from '../assets/divert-logo-dark.svg';

interface NavEntry {
  label: string;
  href: string;
  match: (path: string) => boolean;
  feature: Feature;
  chip?: ReactNode;
}

const MEMBER_360 = /^\/analytics\/members\//;

function buildNav(features: string[] | null | undefined): { primary: NavEntry[]; operations: NavEntry[] } {
  const nav: { primary: NavEntry[]; operations: NavEntry[] } = {
    primary: [
      {
        label: 'Dashboard',
        feature: 'dashboard',
        href: '/dashboard',
        match: (p) => p === '/dashboard' || p === '/',
      },
      {
        label: 'Members',
        feature: 'members',
        href: '/members',
        match: (p) => p.startsWith('/members') || MEMBER_360.test(p),
      },
      { label: 'Groups', feature: 'groups', href: '/groups', match: (p) => p.startsWith('/groups') },
      { label: 'Contacts', feature: 'contacts', href: '/contacts', match: (p) => p.startsWith('/contacts') },
      { label: 'Analytics', feature: 'analytics', href: '/analytics', match: (p) => p === '/analytics' },
    ],
    operations: [
      { label: 'Website forms', feature: 'forms', href: '/forms', match: (p) => p.startsWith('/forms') },
      { label: 'Webinars', feature: 'webinars', href: '/webinars', match: (p) => p.startsWith('/webinars') },
      { label: 'Messages', feature: 'messages', href: '/messages', match: (p) => p.startsWith('/messages') },
      { label: 'Programs', feature: 'programs', href: '/programs', match: (p) => p.startsWith('/programs') },
      { label: 'Committees', feature: 'committees', href: '/committees', match: (p) => p.startsWith('/committees') },
      { label: 'Events', feature: 'events', href: '/events', match: (p) => p.startsWith('/events') },
      { label: 'Payments', feature: 'payments', href: '/payments', match: (p) => p.startsWith('/payments') },
      { label: 'Stipends', feature: 'stipends', href: '/stipends', match: (p) => p.startsWith('/stipends') },
      {
        label: 'Fellow review',
        feature: 'fellowReview',
        href: '/fellow-review',
        match: (p) => p.startsWith('/fellow-review'),
      },
      { label: 'Sync & Integrations', feature: 'sync', href: '/sync', match: (p) => p.startsWith('/sync') },
      {
        label: 'Reports',
        feature: 'reports',
        href: '/reports',
        match: (p) => p.startsWith('/reports'),
        chip: <PhaseChip label="P3" dashed={false} />,
      },
    ],
  };
  return {
    primary: nav.primary.filter((e) => canSee(features, e.feature)),
    operations: nav.operations.filter((e) => canSee(features, e.feature)),
  };
}

// One image serves every environment, so this must be resolved at runtime —
// import.meta.env.PROD is true for any production build, test included.
function environmentLabel(host: string) {
  if (host.startsWith('test-')) return 'Test';
  if (host === 'localhost' || host.startsWith('127.') || host === '::1') return 'Development';
  return 'Production';
}

const CRUMBS: [RegExp, string][] = [
  [MEMBER_360, 'Member 360'],
  [/^\/dashboard/, 'Dashboard'],
  [/^\/members/, 'Members'],
  [/^\/analytics/, 'Analytics'],
  [/^\/sync/, 'Sync & Integrations'],
  [/^\/forms/, 'Website forms'],
  [/^\/webinars/, 'Webinars'],
  [/^\/reports/, 'Reports'],
  [/^\/groups/, 'Groups'],
  [/^\/contacts/, 'Contacts'],
  [/^\/messages/, 'Messages'],
  [/^\/fellow-review/, 'Fellow review'],
  [/^\/programs/, 'Programs'],
  [/^\/committees/, 'Committees'],
  [/^\/events/, 'Events'],
  [/^\/payments/, 'Payments'],
  [/^\/stipends/, 'Stipends'],
];

function NavItem({ entry, active }: { entry: NavEntry; active: boolean }) {
  return (
    <Link
      to={entry.href}
      aria-current={active ? 'page' : undefined}
      className={`flex items-center justify-between gap-2 min-h-[44px] px-3 rounded-sm text-[15px] leading-5 ${
        active ? 'text-ink font-semibold bg-selected' : 'text-ink-2 hover:text-ink hover:bg-selected'
      }`}
    >
      <span>{entry.label}</span>
      {entry.chip}
    </Link>
  );
}

export default function Layout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const nav = buildNav(user?.features);

  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        buttonRef.current?.focus();
      }
    };
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!menuRef.current?.contains(t) && !buttonRef.current?.contains(t)) setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    menuRef.current?.querySelector<HTMLElement>('a, button')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [menuOpen]);

  const page = CRUMBS.find(([re]) => re.test(location.pathname))?.[1] ?? 'Platform';
  const env = environmentLabel(window.location.hostname);

  const signOut = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-paper border-b border-line">
        <div className="max-w-content mx-auto px-4 md:px-8 py-3 flex items-center gap-6 max-md:gap-3">
          <Link to="/dashboard" className="flex-none" aria-label="DIVERT Mental Health, dashboard">
            <img src={logo} alt="" className="h-[30px] w-auto" />
          </Link>
          <nav aria-label="Breadcrumb" className="flex-1 min-w-0 truncate text-[15px] leading-5 max-md:hidden">
            <span className="text-ink-2">Platform</span>
            <span className="text-ink-3 mx-2" aria-hidden>
              ·
            </span>
            <span className="text-ink font-semibold" aria-current="page">
              {page}
            </span>
          </nav>
          <span className="text-small text-ink-3 max-md:hidden">{env === 'Production' ? null : `${env} copy`}</span>
          <div className="relative ml-auto md:ml-0">
            <button
              ref={buttonRef}
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={menuOpen}
              aria-controls="app-menu"
              className="inline-flex items-center min-h-[44px] rounded-sm border border-line-strong px-4 text-[15px] font-semibold text-ink hover:bg-selected"
            >
              Menu
            </button>
            {menuOpen ? (
              <div
                ref={menuRef}
                id="app-menu"
                className="absolute right-0 top-[52px] w-[min(560px,calc(100vw-32px))] max-h-[calc(100vh-90px)] supports-[height:100dvh]:max-h-[calc(100dvh-90px)] overflow-y-auto overscroll-contain bg-surface border border-line rounded-md shadow-lift p-4 motion-safe:animate-dialog-in max-md:fixed max-md:inset-x-3 max-md:top-auto max-md:bottom-[max(12px,env(safe-area-inset-bottom))] max-md:w-auto max-md:max-h-[calc(100vh-96px)] max-md:supports-[height:100dvh]:max-h-[calc(100dvh-96px)] max-md:motion-safe:animate-sheet-in"
              >
                <nav aria-label="Pages" className="grid grid-cols-2 gap-x-6 max-md:grid-cols-1">
                  <div>
                    {nav.primary.map((e) => (
                      <NavItem key={e.label} entry={e} active={e.match(location.pathname)} />
                    ))}
                  </div>
                  {nav.operations.length ? (
                    <div className="max-md:mt-3 max-md:pt-3 max-md:border-t max-md:border-line">
                      {nav.operations.map((e) => (
                        <NavItem key={e.label} entry={e} active={e.match(location.pathname)} />
                      ))}
                    </div>
                  ) : null}
                </nav>
                <div className="mt-4 pt-4 border-t border-line flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-ink">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className="text-small text-ink-3 break-all">
                      {user?.email} · {roleLabel(user?.role)}
                    </div>
                  </div>
                  <button
                    onClick={signOut}
                    className="inline-flex items-center min-h-[44px] rounded-sm border border-line-strong px-4 text-[15px] font-semibold text-ink hover:bg-selected"
                  >
                    Sign out
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <main className="max-w-content mx-auto px-4 md:px-8 pt-6 md:pt-8 pb-24">{children}</main>
    </div>
  );
}
